import DoubleArrowIcon from '@mui/icons-material/DoubleArrow';
import ReportProblemRoundedIcon from '@mui/icons-material/ReportProblemRounded';
import {
  Alert,
  Button,
  Grid,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Box, Container } from '@mui/system';

import DynamicBreadcrumbs from '@/components/nav/Breadcrumb';
import ResourcesRightSidebar from '@/components/nav/ResourcesRightSidebar';
import Hidden from '@/components/ui/TzoHidden';
import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';

const Form1099PATRInstructions = () => {
  return (
    <AppLayout
      meta={
        <Meta
          title="IRS Form 1099-PATR instructions for Tax Year 2025"
          description="Learn how to fill out IRS Form 1099-PATR for Tax Year 2025 with step-by-step guidance from TaxZerone to accurately report patronage dividends to the IRS."
          keywords=""
          canonical="resources/information-returns/form-1099-patr-instructions/"
          ogType="article"
          jsonLdPricing="2.49"
          jsonLdAggregateRatingValue="5.00"
          jsonLdAggregateRatingCount="57448"
        />
      }
    >
      <Container maxWidth="lg">
        <Typography
          variant="h1"
          sx={{
            fontSize: { lg: 40, md: 36, sm: 32, xs: 24 },
            fontWeight: 600,
            mb: 5,
            mt: 5,
            textAlign: 'center',
          }}
        >
          <span className="highlight-container">
            <span className="highlight">Form 1099-PATR</span>
          </span>{' '}
          Instructions
        </Typography>
      </Container>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          py: 5,
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
        mt={0}
      >
        <Container maxWidth="lg" sx={{ mb: 0 }}>
          <DynamicBreadcrumbs />
          <Grid
            container
            spacing={0}
            rowSpacing={0}
            m={0}
            sx={{ flexDirection: 'row-reverse' }}
          >
            <Grid
              size={{ xs: 12, md: 3.5, sm: 4.5 }}
              sx={{ display: { xs: 'none', sm: 'block' } }}
            >
              <ResourcesRightSidebar />
            </Grid>
            <Grid size={{ xs: 12, md: 8.5, sm: 7.5 }}>
              <Paper
                variant="outlined"
                sx={{
                  border: '1px solid #ccc',
                  p: 3,
                  px: 3,
                  textAlign: 'left',
                  mb: 0,
                }}
              >
                <Box
                  itemScope
                  itemType="https://schema.org/Article"
                  sx={{
                    p: 3,
                    px: 3,
                    backgroundColor: '#f0f5f8',
                    borderLeft: '5px solid #01456c',
                    mb: 4,
                    borderRadius: 3,
                    position: 'relative',
                  }}
                >
                  <link
                    itemProp="mainEntityOfPage"
                    href="https://www.taxzerone.com/resources/information-returns/form-1099-patr-instructions/"
                  />

                  {/* Article requires an image */}
                  <div
                    itemProp="image"
                    itemScope
                    itemType="https://schema.org/ImageObject"
                    style={{ display: 'none' }}
                  >
                    <link
                      itemProp="url"
                      href="https://www.taxzerone.com/assets/images/logo.webp"
                    />
                    <data itemProp="width" value="380" />
                    <data itemProp="height" value="124" />
                  </div>

                  <Box
                    sx={{
                      display: 'grid',
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                      textAlign: 'left',
                      mb: 2,
                    }}
                  >
                    <Typography variant="body2">
                      Last updated:{' '}
                      <time
                        itemProp="datePublished"
                        dateTime="2026-07-07T09:00:00-05:00"
                      >
                        July 7, 2026
                      </time>
                      <time
                        itemProp="dateModified"
                        dateTime="2026-07-07T09:00:00-05:00"
                        style={{ display: 'none' }}
                      >
                        July 7, 2026
                      </time>
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600 }}
                      itemProp="author"
                      itemScope
                      itemType="https://schema.org/Person"
                    >
                      -by{' '}
                      <span itemProp="name">
                        <a
                          itemProp="url"
                          href={`${process.env.NEXT_PUBLIC_HOMESITE_URL}`}
                          style={{ color: 'inherit', textDecoration: 'none' }}
                        >
                          Alexia Zepeda
                        </a>
                      </span>{' '}
                      | TaxZerone
                    </Typography>
                  </Box>

                  <Typography
                    variant="h4"
                    itemProp="headline"
                    sx={{
                      fontWeight: 600,
                      mb: 2,
                      fontSize: { md: 20, sm: 18, xs: 16 },
                      color: '#01456c',
                      fontStyle: 'italic',
                    }}
                  >
                    Form 1099-PATR at a Glance
                  </Typography>

                  <Box
                    component="ul"
                    itemScope
                    itemType="https://schema.org/ItemList"
                    sx={{
                      mb: 0,
                      pl: 0,
                      listStyle: 'none',

                      '& li': {
                        mb: 1.6,
                        position: 'relative',
                        pl: 3,
                      },

                      '& li::before': {
                        content: '"★"',
                        position: 'absolute',
                        left: 0,
                        top: '-2px',
                        color: '#01456c',
                        fontSize: '18px',
                      },
                    }}
                  >
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="1" />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>
                          {' '}
                          Form 1099-PATR,{' '}
                        </strong>{' '}
                        reports taxable distributions received from
                        cooperatives, including patronage dividends,
                        nonpatronage distributions, per-unit retain allocations,
                        qualified payments, and federal income tax withheld.
                      </span>
                    </li>

                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="2" />
                      <span itemProp="name">
                        Recipient copies must be furnished by{' '}
                        <strong style={{ fontWeight: 600 }}> January 31</strong>
                        , and the form must be filed with the IRS by{' '}
                        <strong style={{ fontWeight: 600 }}>
                          {' '}
                          February 28
                        </strong>{' '}
                        if filing on paper or{' '}
                        <strong style={{ fontWeight: 600 }}>
                          {' '}
                          March 31{' '}
                        </strong>{' '}
                        if filing electronically.{' '}
                      </span>
                    </li>

                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="3" />
                      <span itemProp="name">
                        This article covers line-by-line instructions including
                        payer and recipient information, Account Number, FATCA
                        Filing Requirement Checkbox,{' '}
                        <strong style={{ fontWeight: 600 }}>Box 1</strong>{' '}
                        (Patronage Dividends),{' '}
                        <strong style={{ fontWeight: 600 }}>Box 2</strong>{' '}
                        (Nonpatronage Distributions),
                        <strong style={{ fontWeight: 600 }}>Box 3</strong>{' '}
                        (Per-Unit Retain Allocations),{' '}
                        <strong style={{ fontWeight: 600 }}>Box 4</strong>{' '}
                        (Federal Income Tax Withheld),{' '}
                        <strong style={{ fontWeight: 600 }}>Box 5</strong>{' '}
                        (Redeemed Nonqualified Notices),{' '}
                        <strong style={{ fontWeight: 600 }}>Box 6</strong>{' '}
                        (Section 199A(g) Qualified Payments),{' '}
                        <strong style={{ fontWeight: 600 }}>Box 7</strong>{' '}
                        (Qualified Payments Received),{' '}
                        <strong style={{ fontWeight: 600 }}>Box 8</strong>{' '}
                        (Section 199A(g) Deductions Passed Through),{' '}
                        <strong style={{ fontWeight: 600 }}>Box 9</strong>{' '}
                        (Investment Credit),{' '}
                        <strong style={{ fontWeight: 600 }}>Box 10</strong>{' '}
                        (Work Opportunity Credit),{' '}
                        <strong style={{ fontWeight: 600 }}>Box 11</strong>{' '}
                        (Other Credits and Deductions),{' '}
                        <strong style={{ fontWeight: 600 }}>Box 12</strong>{' '}
                        (Specified Cooperative), and{' '}
                        <strong style={{ fontWeight: 600 }}>Boxes 13-15</strong>{' '}
                        (State Information)
                      </span>
                    </li>

                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="4" />
                      <span itemProp="name">
                        Late or incorrect filing may result in IRS penalties
                        ranging from
                        <strong style={{ fontWeight: 600 }}>
                          {' '}
                          $60 to $680 per return
                        </strong>
                        , depending on how late the form is filed or whether
                        there is intentional disregard.
                      </span>
                    </li>

                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="5" />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>
                          {' '}
                          Extensions:
                        </strong>{' '}
                        <Link
                          href="/tax-extension/efile-irs-form-8809-online/"
                          sx={{ color: '#0202ff' }}
                        >
                          File Form 8809
                        </Link>{' '}
                        to request an automatic filing extension.{' '}
                        <Link
                          href="/tax-extension/file-irs-form-15397-online/"
                          sx={{ color: '#0202ff' }}
                        >
                          File Form 15397
                        </Link>{' '}
                        to request the full{' '}
                        <strong style={{ fontWeight: 600 }}>
                          30-day extension
                        </strong>{' '}
                        for furnishing recipient copies.
                      </span>
                    </li>
                  </Box>

                  {/* Publisher needs an Organization with a logo ImageObject — no meta tags, same pattern */}
                  <div
                    itemProp="publisher"
                    itemScope
                    itemType="https://schema.org/Organization"
                    style={{ display: 'none' }}
                  >
                    <span itemProp="name">TaxZerone</span>
                    <div
                      itemProp="logo"
                      itemScope
                      itemType="https://schema.org/ImageObject"
                    >
                      <link
                        itemProp="url"
                        href="https://www.taxzerone.com/assets/images/logo.webp"
                      />
                      <data itemProp="width" value="380" />
                      <data itemProp="height" value="124" />
                    </div>
                  </div>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  What is IRS Form 1099-PATR?
                </Typography>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  IRS Form 1099-PATR, &quot;Taxable Distributions Received From
                  Cooperatives, &quot; reports distributions, patronage
                  dividends, or similar payments of $10 or more made by
                  cooperatives to their members. The payer must file this form
                  to notify both the IRS and recipients about taxable income.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Who needs to file Form 1099-PATR?
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Any cooperative that pays $10 or more in patronage dividends
                  or other distributions to its members during the tax year must
                  <Link
                    href="/information-returns/efile-irs-form-1099-patr/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    file Form 1099-PATR
                  </Link>
                  . This requirement applies to agricultural cooperatives, rural
                  electric cooperatives, and any other type of cooperative that
                  distributes taxable income to its members.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  When is the Form 1099-PATR due date?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Payers must be aware of two important deadlines related to
                  Form 1099-PATR:
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                    mb: 1,
                    fontWeight: 600,
                  }}
                >
                  Filing deadline with the IRS
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  The deadline for payers to submit Form 1099-PATR to the IRS
                  depends on the filing method:
                </Typography>
                <ul style={{ marginBottom: 32 }}>
                  <li style={{ marginBottom: 16 }}>
                    <strong>Electronic filing deadline: March 31, 2026</strong>
                    <br />
                    If you are e-filing Form 1099-PATR with the IRS, the
                    deadline is March 31st of the year following the tax year
                    for which the form is being issued.
                  </li>

                  <Box
                    component="div"
                    sx={{ mb: 2, p: 2, borderRadius: 2 }}
                    className="gradient-bg"
                  >
                    <Stack
                      direction={{ xs: 'column', md: 'row' }}
                      spacing={2}
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Box sx={{ width: '100%' }}>
                        <Typography
                          variant="body1"
                          sx={{
                            fontWeight: 600,
                            mb: 1,
                          }}
                        >
                          Avoid the stress of last-minute 1099-PATR deadlines.
                        </Typography>
                        <Typography variant="body2">
                          E-file with TaxZerone now and enjoy hassle-free IRS
                          compliance and peace of mind.
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          width: '100%',
                          maxWidth: 150,
                          textAlign: 'center',
                        }}
                      >
                        <Button
                          variant="contained"
                          sx={{
                            textTransform: 'none',
                            boxShadow: 'none',
                            borderRadius: 2,
                            backgroundColor: '#fff',
                            color: '#333',
                            '&:hover, &:focus, &:active': { color: '#fff' },
                          }}
                          size="large"
                          href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=1099patrinstructions`}
                        >
                          E-File Now
                        </Button>
                      </Box>
                    </Stack>
                  </Box>
                  <li style={{ marginBottom: 16 }}>
                    <strong>Paper filing deadline: February 28, 2026*</strong>
                    <br /> If you are filing Form 1099-PATR with the IRS using
                    traditional paper methods, the deadline is February 28th of
                    the year following the tax year for which the form is
                    issued.
                  </li>
                </ul>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                    mb: 1,
                    fontWeight: 600,
                  }}
                >
                  Recipient copies deadline: January 31, 2026*
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Payers must deliver copies of Form 1099-PATR to recipients by
                  January 31st of the year following the relevant tax year. This
                  allows recipients to accurately file their tax returns on
                  time.
                </Typography>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mb: 3, mt: 0, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <b> Note:</b> * if the due date falls on the weekend or
                  federal holiday, it is extended to next business days.
                </Alert>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Missing these deadlines or providing incorrect information on
                  Form 1099-PATR can lead to IRS penalties. To avoid potential
                  penalties, payers should take steps to ensure that Form
                  1099-PATR is filed and distributed both accurately and on
                  time.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Form 1099-PATR penalty
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If you miss{' '}
                  <Link
                    href="/information-returns/efile-irs-form-1099-patr/"
                    sx={{ color: '#0202ff' }}
                  >
                    filing Form 1099-PATR
                  </Link>{' '}
                  within the deadline, the IRS will impose a penalty. Here are
                  the Form 1099-PATR penalty rates:
                </Typography>
                <Box component="div" sx={{ mb: 2 }}>
                  <TableContainer sx={{ border: '1px solid #828282' }}>
                    <Table sx={{}} aria-label="Due Date Chart">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#ccc' }}>
                          <TableCell sx={{ color: '#444', width: 300 }}>
                            Days late
                          </TableCell>

                          <TableCell sx={{ color: '#444', width: 300 }}>
                            Penalty per return
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow sx={{}}>
                          <TableCell>Up to 30 days</TableCell>
                          <TableCell>$60</TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>31 days late through August 1</TableCell>
                          <TableCell>$130</TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell>After August 1 or not filed</TableCell>
                          <TableCell>$340</TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>Intentional disregard</TableCell>
                          <TableCell>$680</TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
                <Stack
                  direction={{ md: 'row', sm: 'column' }}
                  spacing={2}
                  alignItems="center"
                  sx={{
                    mb: 5,
                    p: 2,
                    borderRadius: 2,
                    backgroundColor: '#f9f9f9',
                    backgroundSize: {
                      xs: '20% auto',
                      sm: '20% auto',
                      md: '20% auto',
                    },
                    border: '1px solid #ccc',
                  }}
                >
                  <Box sx={{ width: '100%', mb: { md: 0, sm: 1, xs: 2 } }}>
                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: 600,
                        mb: 0.5,
                        fontSize: '20px !important',
                      }}
                    >
                      Why risk penalties?
                    </Typography>
                    <Typography variant="body2">
                      E-file your 1099-PATR forms ahead of the deadline with
                      TaxZerone.
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      width: '100%',
                      maxWidth: 230,
                      textAlign: 'left',
                    }}
                  >
                    <Button
                      variant="contained"
                      color="primary"
                      sx={{
                        textTransform: 'none',
                        boxShadow: 'none',
                        borderRadius: 2,
                      }}
                      size="large"
                      href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=1099patrinstructions`}
                    >
                      E-file Form 1099-PATR
                    </Button>
                  </Box>
                </Stack>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Form 1099-PATR Instructions - How to fill out?
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Let&apos;s see line-by-line instructions on how to fill out
                  Form 1099-PATR.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 1,
                    fontWeight: 600,
                  }}
                >
                  Payer details
                </Typography>
                <picture>
                  <source srcSet="/assets/images/form-1099-patr-payer-details.webp" />
                  <source srcSet="/assets/images/form-1099-patr-payer-details.png" />
                  <img
                    src="/assets/images/form-1099-patr-payer-details.png"
                    loading="lazy"
                    alt="Payer details"
                    width={525}
                    height={208}
                    className="proportional-img"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter your name or business name, complete address, and TIN
                  (SSN if you&apos;re an individual; EIN if you&apos;re a
                  business)
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    mt: 2,
                    fontWeight: 600,
                  }}
                >
                  Recipient details
                </Typography>
                <picture>
                  <source srcSet="/assets/images/form-1099-patr-recipient.webp" />
                  <source srcSet="/assets/images/form-1099-patr-recipient.png" />
                  <img
                    src="/assets/images/form-1099-patr-recipient.png"
                    loading="lazy"
                    alt="Recipient details"
                    width={525}
                    height={285}
                    className="proportional-img"
                  />
                </picture>
                <Typography variant="body1" sx={{ mt: 1, mb: 1 }}>
                  Enter the recipient’s name, complete address, and TIN (SSN for
                  an individual; EIN for a business).
                </Typography>
                <ul style={{ marginBottom: 30 }}>
                  <li style={{ marginBottom: 12 }}>
                    <b>
                      <i>Account number:</i>
                    </b>{' '}
                    You must provide the account number if you have multiple
                    accounts for the same recipient and filing more than one
                    Form 1099-PATR for that recipient.
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    <b>
                      <i> 2nd TIN not:</i>
                    </b>{' '}
                    If the IRS has notified you two times within the last 3
                    years that the payee provided an incorrect TIN, you can
                    enter an &quot;X&quot; in this box. By doing so, the IRS
                    will stop sending you further notices about this
                    account&apos;s TIN.
                  </li>
                </ul>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 1: Patronage Dividends
                </Typography>
                <picture>
                  <source srcSet="/assets/images/form-1099-patr-patronage-divi.webp" />
                  <source srcSet="/assets/images/form-1099-patr-patronage-divi.png" />
                  <img
                    src="/assets/images/form-1099-patr-patronage-divi.png"
                    loading="lazy"
                    alt="Box 1: Patronage Dividends"
                    width={217}
                    height={53}
                    className="proportional-img"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Enter the member&apos;s share of total patronage dividends,
                  including:
                </Typography>
                <ol style={{ marginBottom: 30 }}>
                  <li style={{ marginBottom: 16 }}>Cash payments</li>
                  <li style={{ marginBottom: 16 }}>Qualified checks</li>
                  <li style={{ marginBottom: 16 }}>
                    Qualified written notices of allocation (face value)
                  </li>
                  <li style={{ marginBottom: 16 }}>Other property</li>
                </ol>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mb: 3, mt: 0, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <b> Note:</b> Do not include nonqualified written notices of
                  allocation.
                </Alert>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 2: Nonpatronage Distributions
                </Typography>
                <picture>
                  <source srcSet="/assets/images/form-1099-patr-non-patronagei.webp" />
                  <source srcSet="/assets/images/form-1099-patr-non-patronagei.png" />
                  <img
                    src="/assets/images/form-1099-patr-non-patronagei.png"
                    loading="lazy"
                    alt="Box 2: Nonpatronage Distributions"
                    width={217}
                    height={53}
                    className="proportional-img"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  This box applies only to farmers&apos; cooperatives exempt
                  from tax under section 521.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Enter the patron&apos;s share of distributions from
                  nonpatronage sources, including:
                </Typography>
                <ol style={{ marginBottom: 20 }}>
                  <li style={{ marginBottom: 12 }}>
                    Earnings from business done with:
                  </li>
                  <ul style={{ marginBottom: 20 }}>
                    <li style={{ marginBottom: 12 }}>
                      The United States government
                    </li>
                    <li style={{ marginBottom: 12 }}>
                      Any U.S. government agency
                    </li>
                  </ul>
                  <li style={{ marginBottom: 12 }}>
                    Other nonpatronage income allowed as a deduction under
                    section 1382(c)(2)(A)
                  </li>
                </ol>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Include the following types of payments:
                </Typography>
                <ul style={{ marginBottom: 20 }}>
                  <li style={{ marginBottom: 12 }}>
                    Cash (including qualified or &quot;consent&quot; checks)
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    Qualified written notices of allocation (at face value)
                  </li>
                  <li style={{ marginBottom: 12 }}>Other property</li>
                  <li style={{ marginBottom: 12 }}>
                    Nonqualified written notices of allocation
                  </li>
                </ul>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  These distributions must be made on a patronage basis.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 3: Per-Unit Retain Allocations
                </Typography>
                <picture>
                  <source srcSet="/assets/images/form-1099-patr-per-unit-retain.webp" />
                  <source srcSet="/assets/images/form-1099-patr-per-unit-retain.png" />
                  <img
                    src="/assets/images/form-1099-patr-per-unit-retain.png"
                    loading="lazy"
                    alt="Box 3: Per-Unit Retain Allocations"
                    width={217}
                    height={54}
                    className="proportional-img"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Enter the patron&apos;s share of total per-unit retain
                  allocations, including:
                </Typography>
                <ol style={{ marginBottom: 30 }}>
                  <li style={{ marginBottom: 12 }}>Cash payments</li>
                  <li style={{ marginBottom: 12 }}>
                    Qualified per-unit retain certificates (at face value)
                  </li>{' '}
                  <li style={{ marginBottom: 12 }}>Other property</li>
                </ol>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Do not include:
                </Typography>
                <ul style={{ marginBottom: 30 }}>
                  <li style={{ marginBottom: 12 }}>
                    Nonqualified per-unit retain certificates
                  </li>
                </ul>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mb: 3, mt: 0, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <b> Note:</b> Only include allocations that are deductible
                  under section 1382(b)(3).
                </Alert>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,

                    fontWeight: 600,
                  }}
                >
                  Box 4: Federal Income Tax Withheld
                </Typography>
                <picture>
                  <source srcSet="/assets/images/form-1099-patr-federal-income-tax.webp" />
                  <source srcSet="/assets/images/form-1099-patr-federal-income-tax.png" />
                  <img
                    src="/assets/images/form-1099-patr-federal-income-tax.png"
                    loading="lazy"
                    alt="Box 4: Federal Income Tax Withheld"
                    width={217}
                    height={54}
                    className="proportional-img"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter the amount of backup withholding on patronage payments.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 5: Redeemed Nonqualified Notices
                </Typography>
                <picture>
                  <source srcSet="/assets/images/form-1099-patr-redeemed-non-qualified.webp" />
                  <source srcSet="/assets/images/form-1099-patr-redeemed-non-qualified.png" />
                  <img
                    src="/assets/images/form-1099-patr-redeemed-non-qualified.png"
                    loading="lazy"
                    alt="Box 5: Redeemed Nonqualified Notices"
                    width={217}
                    height={54}
                    className="proportional-img"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Enter the total amount of two types of redeemed nonqualified
                  notices:
                </Typography>
                <ol style={{ marginBottom: 30 }}>
                  <li style={{ marginBottom: 12 }}>
                    Nonqualified written notices of allocation:
                  </li>
                  <ul style={{ marginBottom: 10 }}>
                    {' '}
                    <li style={{ marginBottom: 12 }}>
                      Paid as patronage dividends
                    </li>
                  </ul>
                  <li style={{ marginBottom: 12 }}>
                    Nonqualified per-unit retain certificates:
                  </li>
                  <ul style={{ marginBottom: 10 }}>
                    {' '}
                    <li style={{ marginBottom: 12 }}>
                      Paid as per-unit retain allocations
                    </li>
                  </ul>
                </ol>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 6: Section 199A(g) Deduction
                </Typography>
                <picture>
                  <source srcSet="/assets/images/form-1099-patr-section-199a-deduction.webp" />
                  <source srcSet="/assets/images/form-1099-patr-section-199a-deduction.png" />
                  <img
                    src="/assets/images/form-1099-patr-section-199a-deduction.png"
                    loading="lazy"
                    alt="Box 6: Section 199A(g) Deduction"
                    width={217}
                    height={80}
                    className="proportional-img"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  This box applies only to specified agricultural and
                  horticultural cooperatives.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the patron&apos;s share of the cooperative&apos;s
                  section 199A(g) deduction.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Requirements:
                </Typography>
                <ol style={{ marginBottom: 20 }}>
                  <li style={{ marginBottom: 12 }}>
                    The cooperative must have claimed this deduction under
                    section 199A(g).
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    The cooperative must have notified the patron in writing
                    within the payment period specified in section 1382(d).
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    The deduction allocated to each patron cannot exceed 9% of
                    the qualified payments reported in box 7.
                  </li>
                </ol>
                <ul style={{ marginBottom: 20 }}>
                  <li style={{ marginBottom: 12 }}>
                    Only eligible taxpayers may claim this passed-through
                    deduction.
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    C corporations that are not specified agricultural or
                    horticultural cooperatives are not eligible.
                  </li>
                </ul>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Requirements:
                </Typography>
                <ul style={{ marginBottom: 20 }}>
                  <li style={{ marginBottom: 12 }}>
                    Do not reduce amounts in box 1 or box 3 by the amount
                    reported in box 6.
                  </li>
                </ul>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 7: Qualified Payments
                </Typography>
                <picture>
                  <source srcSet="/assets/images/form-1099-patr-qualified-payment.webp" />
                  <source srcSet="/assets/images/form-1099-patr-qualified-payment.png" />
                  <img
                    src="/assets/images/form-1099-patr-qualified-payment.png"
                    loading="lazy"
                    alt="Box 7: Qualified Payments"
                    width={217}
                    height={80}
                    className="proportional-img"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the total qualified payments paid to the patron.
                </Typography>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mb: 3, mt: 0, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <b> Note:</b> This box applies only to specified agricultural
                  and horticultural cooperatives.
                </Alert>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Reporting requirement:
                </Typography>
                <ul style={{ marginBottom: 30 }}>
                  <li style={{ marginBottom: 12 }}>
                    You must report this information regardless of whether you
                    pass any section 199A(g) deduction through to the patrons.
                  </li>
                </ul>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 8: Section 199A(a) Qualified Items
                </Typography>
                <picture>
                  <source srcSet="/assets/images/form-1099-patr-qualified-items.webp" />
                  <source srcSet="/assets/images/form-1099-patr-qualified-items.png" />
                  <img
                    src="/assets/images/form-1099-patr-qualified-items.png"
                    loading="lazy"
                    alt="Box 8: Section 199A(a) Qualified Items"
                    width={217}
                    height={54}
                    className="proportional-img"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter amounts reported to patrons that are qualified items
                  from trades or businesses eligible for section 199A
                  deductions.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Eligible items:
                </Typography>
                <ul style={{ marginBottom: 20 }}>
                  <li style={{ marginBottom: 12 }}>
                    Income, gain, deduction, or loss from qualified trades or
                    businesses
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    Must be effectively connected with a U.S. trade or business
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    Items from specified service trades or businesses (SSTBs)
                  </li>
                </ul>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Exclude:
                </Typography>
                <ul style={{ marginBottom: 20 }}>
                  <li style={{ marginBottom: 12 }}>Tax-exempt income</li>
                  <li style={{ marginBottom: 12 }}>Capital gains</li>
                  <li style={{ marginBottom: 12 }}>
                    Income not effectively connected with a U.S. trade or
                    business
                  </li>
                </ul>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 9: Section 199A(a) SSTB Items
                </Typography>
                <picture>
                  <source srcSet="/assets/images/form-1099-patr-section-sstb-items.webp" />
                  <source srcSet="/assets/images/form-1099-patr-section-sstb-items.png" />
                  <img
                    src="/assets/images/form-1099-patr-section-sstb-items.png"
                    loading="lazy"
                    alt=" Box 9: Section 199A(a) SSTB Items"
                    width={217}
                    height={54}
                    className="proportional-img"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter amounts reported to patrons that are qualified items
                  from specified service trades or businesses (SSTBs) under
                  section 199A.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  SSTB examples:
                </Typography>
                <ul style={{ marginBottom: 20 }}>
                  <li style={{ marginBottom: 12 }}>Health services</li>
                  <li style={{ marginBottom: 12 }}>Legal services</li>
                  <li style={{ marginBottom: 12 }}>Accounting services</li>
                  <li style={{ marginBottom: 12 }}>
                    Other professional services (see Form 8995-A instructions
                    for a full list)
                  </li>
                </ul>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Important notes:
                </Typography>
                <ol style={{ marginBottom: 20 }}>
                  <li style={{ marginBottom: 12 }}>
                    Only report SSTB items in this box
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    These items are from trades or businesses that would
                    otherwise qualify for section 199A deductions.
                  </li>
                </ol>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 10: Investment Credit
                </Typography>
                <picture>
                  <source srcSet="/assets/images/form-1099-patr-investment-credit.webp" />
                  <source srcSet="/assets/images/form-1099-patr-investment-credit.png" />
                  <img
                    src="/assets/images/form-1099-patr-investment-credit.png"
                    loading="lazy"
                    alt="Box 10: Investment Credit"
                    width={217}
                    height={54}
                    className="proportional-img"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter the patron’s share of the total investment credit.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 11: Work Opportunity Credit
                </Typography>
                <picture>
                  <source srcSet="/assets/images/form-1099-patr-work-opportunity.webp" />
                  <source srcSet="/assets/images/form-1099-patr-work-opportunity.png" />
                  <img
                    src="/assets/images/form-1099-patr-work-opportunity.png"
                    loading="lazy"
                    alt="Box 11: Work Opportunity Credit"
                    width={217}
                    height={54}
                    className="proportional-img"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter the patron’s share of the total work opportunity credit.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 12: Other Credits and Deductions
                </Typography>
                <picture>
                  <source srcSet="/assets/images/form-1099-patr-other-credits.webp" />
                  <source srcSet="/assets/images/form-1099-patr-other-credits.png" />
                  <img
                    src="/assets/images/form-1099-patr-other-credits.png"
                    loading="lazy"
                    alt="Box 12: Other Credits and Deductions"
                    width={309}
                    height={53}
                    className="proportional-img"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Select any of the following credits and enter the respective
                  amount in box 12:
                </Typography>
                <ol style={{ marginBottom: 20 }}>
                  <li style={{ marginBottom: 12 }}>
                    Empowerment zone employment (Form 8844)
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    Low sulfur diesel fuel production (Form 8896)
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    Small employer health insurance premiums (Form 8941)
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    Employer differential wage payments (Form 8932)
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    Biodiesel, renewable diesel, or sustainable aviation fuel
                    mixture (Form 8864)
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    Capital costs for small refiner cooperatives complying with
                    EPA sulfur regulations
                  </li>
                </ol>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 13: Specified Cooperatives
                </Typography>
                <picture>
                  <source srcSet="/assets/images/form-1099-patr-specified-coop.webp" />
                  <source srcSet="/assets/images/form-1099-patr-specified-coop.png" />
                  <img
                    src="/assets/images/form-1099-patr-specified-coop.png"
                    loading="lazy"
                    alt="Box 13: Specified Cooperatives"
                    width={125}
                    height={53}
                    className="proportional-img"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Check this box if you are a specified agricultural or
                  horticultural cooperative.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  You are a specified cooperative if:
                </Typography>
                <ol style={{ marginBottom: 30 }}>
                  <li style={{ marginBottom: 12 }}>
                    Part 1 of subchapter T of chapter 1 of subtitle A of the
                    Code applies to you, and
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    You are engaged in either:
                  </li>
                  <ol
                    style={{ marginBottom: 20, listStyleType: 'lower-alpha' }}
                  >
                    <li style={{ marginBottom: 12 }}>
                      Manufacturing, producing, growing, or extracting
                      agricultural or horticultural products (in whole or
                      significant part), or
                    </li>
                    <li style={{ marginBottom: 12 }}>
                      Marketing agricultural or horticultural products.
                    </li>
                  </ol>
                </ol>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  How to File Form 1099-PATR
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  You can file Form 1099-PATR, either electronically or by mail.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Electronic Filing (E-filing)
                </Typography>{' '}
                <Typography variant="body1" sx={{ mb: 2 }}>
                  E-filing is the IRS-recommended method due to its efficiency,
                  accuracy, and quick processing notifications.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  With{' '}
                  <Link href="/" sx={{ color: '#0202ff' }}>
                    {' '}
                    TaxZerone
                  </Link>
                  , you can{' '}
                  <Link
                    href="/information-returns/efile-irs-form-1099-patr/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    e-file Form 1099-PATR
                  </Link>{' '}
                  easily and securely. It simplifies the entire e-filing
                  process, allowing you to complete and submit your form to the
                  IRS within minutes.
                </Typography>
                <ol style={{ marginBottom: 32 }}>
                  <li style={{ marginBottom: 16 }}>
                    Enter the required information, including:
                  </li>
                  <ol style={{ listStyleType: 'circle' }}>
                    <li style={{ marginBottom: 16 }}>
                      Payer&apos;s name, address, and TIN
                    </li>
                    <li style={{ marginBottom: 16 }}>
                      Recipient&apos;s name, address, and TIN
                    </li>
                    <li style={{ marginBottom: 16 }}>Payment details</li>
                  </ol>
                  <li style={{ marginBottom: 16 }}>
                    Review all entered information for accuracy.
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    Transmit the form securely to the IRS.
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    Provide a copy to the recipient.
                  </li>
                </ol>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Paper filing
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  While less efficient, paper filing remains an option for those
                  who prefer it. Here are the steps for paper filing:
                </Typography>
                <ol style={{ marginBottom: 32 }}>
                  <li style={{ marginBottom: 16 }}>
                    Download Form 1099-PATR from the official IRS website (
                    <Link
                      href="https://www.irs.gov"
                      target="_blank"
                      sx={{ color: '#0202ff' }}
                    >
                      www.irs.gov
                    </Link>
                    ).
                  </li>
                  <li style={{ marginBottom: 16 }}>Print the form.</li>
                  <Alert
                    severity="warning"
                    icon={false}
                    sx={{
                      mb: 3,
                      mt: 0,
                      borderLeft: '3px solid rgb(102, 60, 0)',
                    }}
                  >
                    <b> Note:</b> The IRS requires special red ink forms for
                    submission. You can order these from the IRS or approved
                    vendors.
                  </Alert>
                  <li style={{ marginBottom: 16 }}>
                    Complete all required fields using black ink. This includes:
                  </li>
                  <ol style={{ listStyleType: 'circle' }}>
                    <li style={{ marginBottom: 12 }}>
                      Payer&apos;s information
                    </li>
                    <li style={{ marginBottom: 12 }}>
                      Recipient&apos;s information
                    </li>
                    <li style={{ marginBottom: 12 }}>Payment details</li>
                  </ol>
                  <li style={{ marginBottom: 16 }}>
                    Make a copy for your records.
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    Mail the original form to the IRS address specified in the
                    Form 1099 General Instructions. The address varies based on
                    your state. Refer to the Where to send Form 1099-PATR -
                    <Link href="#mail1099patr" sx={{ color: '#0202ff' }}>
                      {' '}
                      Mailing address
                    </Link>{' '}
                    section to get the address for your state.
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    Send a copy to the recipient by February 15th of the year
                    following the reportable transaction.
                  </li>
                </ol>
                <Box
                  component="div"
                  sx={{
                    backgroundColor: '#caf0f8',
                    borderLeft: '5px solid #0077b6',
                    p: 1,
                    mb: 4,
                    borderRadius: 2,
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{ mb: 1, fontWeight: 600, display: 'flex' }}
                  >
                    <ReportProblemRoundedIcon
                      sx={{ mr: 1, color: 'rgb(237, 108, 2)' }}
                    />{' '}
                    Important considerations for paper filing
                  </Typography>
                  <ul style={{ marginBottom: 8 }}>
                    <li style={{ marginBottom: 16 }}>
                      Mail forms several weeks before the deadline to ensure
                      timely arrival.
                    </li>
                    <li style={{ marginBottom: 16 }}>
                      Use certified mail for proof of timely filing.
                    </li>
                    <li style={{ marginBottom: 0 }}>
                      Ensure all copies are legible.
                    </li>
                  </ul>
                </Box>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Alternatively, you can{' '}
                  <Link
                    href="/information-returns/efile-irs-form-1099-patr/"
                    sx={{ color: '#0202ff' }}
                    id="1099-r-mailing-address"
                  >
                    file Form 1099-PATR electronically
                  </Link>{' '}
                  for faster processing and delivery, reducing the risk of
                  delays or lost forms.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Where to send Form 1099-PATR - Mailing address
                </Typography>
                <Typography variant="body1" id="mail1099patr" sx={{ mb: 3 }}>
                  If you prefer paper filing, the mailing address for Form
                  1099-PATR varies depending on your business location. Below is
                  a table summarizing the mailing address for Form 1099-PATR:
                </Typography>
                <Box component="div" sx={{ mb: 5 }}>
                  <TableContainer sx={{ border: '1px solid #828282' }}>
                    <Table sx={{}} aria-label="Due Date Chart">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#ccc' }}>
                          <TableCell sx={{ color: '#444', width: 300 }}>
                            If your business operates in or your legal residence
                            is…
                          </TableCell>

                          <TableCell sx={{ color: '#444', width: 300 }}>
                            Mail Form 1099-PATR to…
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow sx={{}}>
                          <TableCell>
                            Alabama, Arizona, Arkansas, Delaware, Florida,
                            Georgia, Kentucky, Maine, Massachusetts,
                            Mississippi, New Hampshire, New Jersey, New Mexico,
                            New York, North Carolina, Ohio, Texas, Vermont,
                            Virginia
                          </TableCell>
                          <TableCell>
                            Internal Revenue Service
                            <br />
                            Austin Submission Processing Center
                            <br />
                            P.O. Box 149213
                            <br />
                            Austin, TX 78714
                            <br />
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>
                            Alaska, Colorado, Hawaii, Idaho, Illinois, Indiana,
                            Iowa, Kansas, Michigan, Minnesota, Missouri,
                            Montana, Nebraska, Nevada, North Dakota, Oklahoma,
                            Oregon, South Carolina, South Dakota, Tennessee,
                            Utah, Washington, Wisconsin, Wyoming
                          </TableCell>
                          <TableCell>
                            Department of the Treasury
                            <br />
                            IRS Submission Processing Center
                            <br />
                            P.O. Box 219256
                            <br /> Kansas City, MO 64121-9256
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell>
                            California, Connecticut, District of Columbia,
                            Louisiana, Maryland, Pennsylvania, Rhode Island,
                            West Virginia
                          </TableCell>
                          <TableCell>
                            Department of the Treasury
                            <br />
                            IRS Submission Processing Center
                            <br />
                            1973 North Rulon White Blvd.
                            <br />
                            Ogden, UT 84201
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>Outside the United States</TableCell>
                          <TableCell>
                            Internal Revenue Service,
                            <br /> Austin Submission Processing Center,
                            <br /> P.O. Box 149213, <br />
                            Austin, TX 78714
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  How to E-File Form 1099-PATR with TaxZerone
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Before you start{' '}
                  <Link
                    href="/information-returns/efile-irs-form-1099-patr/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    e-filing Form 1099-PATR
                  </Link>
                  , ensure you have all the necessary information to facilitate
                  a smooth and efficient filing experience. Here’s what you need
                  to gather:
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Required information for filing Form 1099-PATR:
                </Typography>
                <ul style={{ marginBottom: 24 }}>
                  <li style={{ marginBottom: 16 }}>Payer details</li>
                  <li style={{ marginBottom: 16 }}>Recipient details</li>
                  <li style={{ marginBottom: 16 }}>
                    The total amount of distributions, including patronage
                    dividends, nonpatronage distributions, and any per-unit
                    retain allocations
                  </li>
                </ul>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Steps to e-file Form 1099-PATR using TaxZerone:
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <b>Step 1:Complete Form 1099-PATR</b>
                  <br />
                  Open Form 1099-PATR and fill out the necessary fields with the
                  information you have gathered.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <b>Step 2: Review and transmit the return</b>
                  <br />
                  Carefully review all the entered information to ensure
                  accuracy. Once verified, transmit the completed Form 1099-PATR
                  to the IRS
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  <b>Step 3: Provide the recipient copy</b>
                  <br />
                  After successfully transmitting the form to the IRS, send a
                  copy of the completed Form 1099-PATR to the recipient.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Benefits of E-Filing Form 1099-PATR with TaxZerone
                </Typography>
                <Typography variant="body1" sx={{ mb: 0 }}>
                  <Link
                    href="/information-returns/efile-irs-form-1099-patr/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    E-filing Form 1099-PATR
                  </Link>{' '}
                  with TaxZerone offers several advantages:
                </Typography>
                <List sx={{ mb: 0 }}>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      <b> IRS Form Validations:</b> TaxZerone&apos;s system
                      checks your returns for errors or missing information,
                      ensuring accuracy before submission.
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      <b>Email Recipient Copies:</b> Conveniently send recipient
                      copies of Form 1099-PATR via email, eliminating the need
                      for manual mailing.
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      <b> Secure Portal Access (ZeroneVault):</b> Recipients can
                      access their return copies anytime, anywhere through the
                      secure ZeroneVault portal.
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      <b>Competitive Pricing:</b> TaxZerone offers
                      industry-leading pricing, with rates starting as low as
                      $0.59 per form, making it affordable for businesses of all
                      sizes.
                    </ListItemText>
                  </ListItem>
                </List>
                <Typography variant="body1">
                  By using TaxZerone&apos;s e-filing solution, you can
                  streamline your Form 1099-PATR filing, save time, reduce
                  errors, and provide a better experience for your recipients.
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>

      <Container maxWidth="lg" sx={{ mb: 5, my: { xs: 6, sm: 6, md: 6 } }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={{ xs: 2, md: 5 }}
        >
          <Box
            sx={{
              width: '100%',
              textAlign: 'center',
            }}
          >
            <Typography
              variant="h2"
              sx={{
                fontSize: { lg: 30, md: 26, sm: 22, xs: 18 },
                fontWeight: 600,
                mb: 3,
                textAlign: 'center',
                lineHeight: 1.5,
              }}
            >
              Maximize the efficiency of your tax reporting by{' '}
              <Hidden smDown>
                <br />
              </Hidden>
              choosing TaxZerone for e-filing Form 1099-PATR.
            </Typography>

            <Box sx={{ textAlign: { xs: 'center', md: 'center' } }}>
              <Button
                variant="contained"
                sx={{
                  mt: 1,
                  transition: 'all 0.3s ease 0s',
                  '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
                }}
                className="btn-primary"
                href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=1099patrinstructions`}
              >
                E-file Form 1099-PATR
              </Button>
              {/* <Typography variant="body2" sx={{ mt: 1 }}>
                Effortless, efficient, and affordable e-filing awaits you.
              </Typography> */}
            </Box>
          </Box>
        </Stack>
      </Container>
    </AppLayout>
  );
};

export default Form1099PATRInstructions;
